/* ==========================================================================
   FILE: global/layouts/MobileNav.jsx
   IAMONEAI - Mobile Bottom Navigation
   Bottom tab bar for mobile navigation between panels
   ========================================================================== */

import React from 'react';
import { PANELS } from './AppLayout';
import './MobileNav.css';

// ==========================================================================
// MOBILE NAV COMPONENT
// ==========================================================================

/**
 * Bottom navigation bar for mobile devices
 * 
 * @param {Object} props
 * @param {string} activePanel - Currently active panel ID
 * @param {Function} onPanelChange - Handler when panel changes
 * @param {string} contextColor - Current context color for theming
 * 
 * @example
 * <MobileNav
 *   activePanel="chat"
 *   onPanelChange={(panel) => setActivePanel(panel)}
 *   contextColor="var(--context-personal)"
 * />
 */
function MobileNav({
  activePanel = PANELS.CHAT,
  onPanelChange,
  contextColor,
  className = '',
}) {
  // Navigation items configuration
  const navItems = [
    {
      id: PANELS.IDENTITY,
      label: 'Identity',
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2" />
          <circle cx="12" cy="7" r="4" />
        </svg>
      ),
      activeIcon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2" />
          <circle cx="12" cy="7" r="4" />
        </svg>
      ),
    },
    {
      id: PANELS.CHAT,
      label: 'Chat',
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
        </svg>
      ),
      activeIcon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
        </svg>
      ),
      primary: true,
    },
    {
      id: PANELS.MIND,
      label: 'Mind',
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M12 2a4 4 0 0 0-4 4c0 1.5.8 2.8 2 3.4V11H8a4 4 0 0 0-4 4c0 1.5.8 2.8 2 3.4V20a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2v-1.6c1.2-.6 2-1.9 2-3.4a4 4 0 0 0-4-4h-2V9.4c1.2-.6 2-1.9 2-3.4a4 4 0 0 0-4-4z" />
        </svg>
      ),
      activeIcon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M12 2a4 4 0 0 0-4 4c0 1.5.8 2.8 2 3.4V11H8a4 4 0 0 0-4 4c0 1.5.8 2.8 2 3.4V20a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2v-1.6c1.2-.6 2-1.9 2-3.4a4 4 0 0 0-4-4h-2V9.4c1.2-.6 2-1.9 2-3.4a4 4 0 0 0-4-4z" />
        </svg>
      ),
    },
  ];

  const navClasses = [
    'mobile-nav',
    className,
  ].filter(Boolean).join(' ');

  return (
    <nav
      className={navClasses}
      style={{ '--nav-context-color': contextColor }}
      role="tablist"
      aria-label="Main navigation"
    >
      {navItems.map((item) => {
        const isActive = activePanel === item.id;

        return (
          <button
            key={item.id}
            type="button"
            className={[
              'mobile-nav-item',
              isActive && 'mobile-nav-item-active',
              item.primary && 'mobile-nav-item-primary',
            ].filter(Boolean).join(' ')}
            onClick={() => onPanelChange?.(item.id)}
            role="tab"
            aria-selected={isActive}
            aria-label={item.label}
          >
            <span className="mobile-nav-icon">
              {isActive ? item.activeIcon : item.icon}
            </span>
            <span className="mobile-nav-label">{item.label}</span>
            {isActive && <span className="mobile-nav-indicator" />}
          </button>
        );
      })}
    </nav>
  );
}

// ==========================================================================
// MOBILE NAV WITH BADGE
// ==========================================================================

/**
 * Mobile nav item with notification badge
 * Used internally for showing unread counts
 */
export function MobileNavBadge({
  count = 0,
  max = 99,
}) {
  if (count <= 0) return null;

  const displayCount = count > max ? `${max}+` : count;

  return (
    <span className="mobile-nav-badge">
      {displayCount}
    </span>
  );
}

// ==========================================================================
// MOBILE CONTEXT BAR
// ==========================================================================

/**
 * Context indicator bar shown above mobile nav
 * Shows current active context with color
 * 
 * @example
 * <MobileContextBar
 *   context="work"
 *   contextLabel="Work"
 *   contextIcon="💼"
 *   onContextClick={() => setShowContextPicker(true)}
 * />
 */
export function MobileContextBar({
  context = 'personal',
  contextLabel = 'Personal',
  contextIcon = '🏠',
  onContextClick,
  className = '',
}) {
  const contextColors = {
    personal: 'var(--context-personal)',
    work: 'var(--context-work)',
    family: 'var(--context-family)',
  };

  const barClasses = [
    'mobile-context-bar',
    className,
  ].filter(Boolean).join(' ');

  return (
    <button
      type="button"
      className={barClasses}
      onClick={onContextClick}
      style={{ '--context-bar-color': contextColors[context] || contextColors.personal }}
    >
      <span className="mobile-context-indicator" />
      <span className="mobile-context-icon">{contextIcon}</span>
      <span className="mobile-context-label">{contextLabel} Mode</span>
      <svg
        className="mobile-context-chevron"
        width="16"
        height="16"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
      >
        <path d="m6 9 6 6 6-6" />
      </svg>
    </button>
  );
}

// ==========================================================================
// MOBILE ACTION BUTTON (FAB)
// ==========================================================================

/**
 * Floating action button for mobile
 * 
 * @example
 * <MobileActionButton
 *   icon={<PlusIcon />}
 *   onClick={handleNewChat}
 *   label="New chat"
 * />
 */
export function MobileActionButton({
  icon,
  onClick,
  label,
  variant = 'primary', // 'primary' | 'secondary'
  position = 'bottom-right', // 'bottom-right' | 'bottom-center'
  className = '',
}) {
  const fabClasses = [
    'mobile-fab',
    `mobile-fab-${variant}`,
    `mobile-fab-${position}`,
    className,
  ].filter(Boolean).join(' ');

  return (
    <button
      type="button"
      className={fabClasses}
      onClick={onClick}
      aria-label={label}
    >
      {icon || (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M12 5v14" />
          <path d="M5 12h14" />
        </svg>
      )}
    </button>
  );
}

// ==========================================================================
// MOBILE QUICK ACTIONS
// ==========================================================================

/**
 * Horizontal scrollable quick action buttons
 * 
 * @example
 * <MobileQuickActions
 *   actions={[
 *     { id: 'goals', label: 'Goals', icon: '🎯', onClick: showGoals },
 *     { id: 'memories', label: 'Memories', icon: '💭', onClick: showMemories },
 *   ]}
 * />
 */
export function MobileQuickActions({
  actions = [],
  className = '',
}) {
  if (actions.length === 0) return null;

  return (
    <div className={`mobile-quick-actions ${className}`.trim()}>
      <div className="mobile-quick-actions-scroll">
        {actions.map((action) => (
          <button
            key={action.id}
            type="button"
            className={[
              'mobile-quick-action',
              action.active && 'mobile-quick-action-active',
            ].filter(Boolean).join(' ')}
            onClick={action.onClick}
          >
            {action.icon && <span className="mobile-quick-action-icon">{action.icon}</span>}
            <span className="mobile-quick-action-label">{action.label}</span>
            {action.badge > 0 && (
              <span className="mobile-quick-action-badge">{action.badge}</span>
            )}
          </button>
        ))}
      </div>
    </div>
  );
}

// ==========================================================================
// MOBILE SHEET HANDLE
// ==========================================================================

/**
 * Drag handle for bottom sheets
 * 
 * @example
 * <MobileSheetHandle />
 */
export function MobileSheetHandle({ className = '' }) {
  return (
    <div className={`mobile-sheet-handle ${className}`.trim()}>
      <div className="mobile-sheet-handle-bar" />
    </div>
  );
}

// ==========================================================================
// MOBILE HEADER
// ==========================================================================

/**
 * Mobile header with back button, title, and actions
 * 
 * @example
 * <MobileHeader
 *   title="Settings"
 *   onBack={() => navigate(-1)}
 *   action={<IconButton icon={<SaveIcon />} />}
 * />
 */
export function MobileHeader({
  title,
  subtitle,
  onBack,
  action,
  transparent = false,
  className = '',
}) {
  const headerClasses = [
    'mobile-header',
    transparent && 'mobile-header-transparent',
    className,
  ].filter(Boolean).join(' ');

  return (
    <header className={headerClasses}>
      <div className="mobile-header-left">
        {onBack && (
          <button
            type="button"
            className="mobile-header-back"
            onClick={onBack}
            aria-label="Go back"
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="m15 18-6-6 6-6" />
            </svg>
          </button>
        )}
      </div>

      <div className="mobile-header-center">
        {title && <h1 className="mobile-header-title">{title}</h1>}
        {subtitle && <p className="mobile-header-subtitle">{subtitle}</p>}
      </div>

      <div className="mobile-header-right">
        {action}
      </div>
    </header>
  );
}

// ==========================================================================
// EXPORTS
// ==========================================================================

export default MobileNav;